/**
 * @file: The state that the toolbar of the calendar shows.
 *
 * The toolbar shows the dates on screen, the view on screen and whether today
 * is on screen. All three come from the view of FullCalendar. This file reads
 * them from the view as plain values, so a test needs no calendar and no DOM.
 */

import moment from 'moment'

/** The unit of each view. The labels of the previous and the next button use it. */
const VIEW_UNITS = Object.freeze({
  timeGridWeek: 'week',
  timeGridDay: 'day'
})

/** The dash between the two dates of the range. It has a space on each side. */
const DATES_DASH = ' – '

/**
 * The state before the first render of the grid. The range is empty, because
 * no dates are on screen yet.
 */
export const NO_DATES = Object.freeze({
  range: '',
  viewType: null,
  unit: 'week',
  todayInView: false
})

/**
 * Write the dates of a range for the title.
 *
 * The text gives a part of the date one time when both ends share it. Thus a
 * week in one month reads "3 – 9 Nov 2026".
 *
 * @param {Date} start - The first day
 * @param {Date} end - The day after the last day, as FullCalendar gives it
 * @returns {string}
 */
export function rangeText (start, end) {
  const first = moment(start)
  const last = moment(end).subtract(1, 'day')

  if (first.isSame(last, 'day')) {
    return first.format('D MMM YYYY')
  }

  if (first.isSame(last, 'month')) {
    return first.format('D') + DATES_DASH + last.format('D MMM YYYY')
  }

  if (first.isSame(last, 'year')) {
    return first.format('D MMM') + DATES_DASH + last.format('D MMM YYYY')
  }

  return first.format('D MMM YYYY') + DATES_DASH + last.format('D MMM YYYY')
}

/**
 * Read the state of the toolbar from a view of FullCalendar.
 *
 * Today is on screen when it is in the current range of the view. FullCalendar
 * used the same test to disable its own Today button.
 *
 * @param {Object|null} view - The view that datesSet gives
 * @param {Date} now
 * @returns {Object} { range, viewType, unit, todayInView }
 */
export function toolbarStateOf (view, now) {
  if (!view) {
    return NO_DATES
  }

  return Object.freeze({
    range: rangeText(view.activeStart, view.activeEnd),
    viewType: view.type,
    unit: VIEW_UNITS[view.type] || NO_DATES.unit,
    todayInView: now >= view.currentStart && now < view.currentEnd
  })
}
